import { useTranslation } from 'react-i18next';

import { Button } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { queryClient } from '@/configuration/react-query';
import { showToast, ToastType } from '@/helpers/toast';
import { useUpdateDeviceScheduleContentMutation } from '@/services';
import { DeviceSchedule } from '@/types';

interface DefaultTabProps {
  deviceSchedule: DeviceSchedule;
  requestSlotInterruptionWarning: (action: () => void | Promise<void>) => boolean;
  refetchDeviceSchedules?: () => void;
}

export const DefaultTab = ({
  deviceSchedule,
  requestSlotInterruptionWarning,
  refetchDeviceSchedules
}: DefaultTabProps) => {
  const { t } = useTranslation('common');
  const { mutateAsync: updateDeviceScheduleContent } = useUpdateDeviceScheduleContentMutation(
    queryClient,
    {
      onSuccess: () => {
        if (refetchDeviceSchedules) {
          refetchDeviceSchedules();
        }
        showToast(ToastType.SUCCESS, t('deviceScheduleUpdatedSuccessfully'));
      },
      onError: () => {
        showToast(ToastType.ERROR, t('somethingWentWrong'));
      }
    }
  );

  const hasPlaylist = !!deviceSchedule.playlist;
  const hasAsset = !!deviceSchedule.asset;
  const hasContent = hasPlaylist || hasAsset;

  const updateToDefault = async () => {
    await updateDeviceScheduleContent({
      id: deviceSchedule.id,
      payload: {
        playlistId: null,
        assetId: null
      }
    });
  };

  const handleSetToDefault = async () => {
    if (requestSlotInterruptionWarning(updateToDefault)) {
      return;
    }

    await updateToDefault();
  };

  return (
    <div className='flex flex-col gap-4 p-6'>
      {hasContent ? (
        <>
          <div className='text-base'>
            {hasPlaylist && (
              <>
                <span className='font-semibold'>{t('selectedPlaylist')}: </span>
                <span>{deviceSchedule.playlist?.name}</span>
              </>
            )}
            {hasAsset && (
              <>
                <span className='font-semibold'>{t('selectedAsset')}: </span>
                <span>{deviceSchedule.asset?.assetName}</span>
              </>
            )}
          </div>
          <Button
            variant={ButtonVariant.PRIMARY}
            handleOnClick={handleSetToDefault}
            className='w-fit'>
            {t('setToDefault')}
          </Button>
        </>
      ) : (
        <div className='text-base text-secondary-steel-blue'>{t('defaultImageIsSet')}</div>
      )}
    </div>
  );
};
